import { useState, useEffect, useMemo, useCallback } from "react";
import { Plus, Pencil, Trash2, Receipt, Download, Calendar, ChevronLeft, ChevronRight, RefreshCw, CreditCard } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  getOneTimeExpenses,
  createOneTimeExpense,
  updateOneTimeExpense,
  deleteOneTimeExpense,
  recordOneTimeExpensePayment,
  resetOneTimeExpensePayment,
  getBankAccounts,
  getCreditCards,
  getStatutoryMonthly,
  formatCurrency,
  formatDate,
  handleNumericInput,
  parseNumericValue,
  formatIndianNumber,
} from "../lib/api";
import { COMPANIES, COMPANY_OPTIONS, getCompanyLabel, COMPANY_COLORS } from "../lib/companies";
import { toast } from "sonner";
import ExportDialog from "../components/ExportDialog";
import GSTInput from "../components/GSTInput";
import { DatePicker } from "../components/DatePicker";
import { ProjectAllocation } from "../components/ProjectAllocation";

const emptyExpense = {
  company: "empact-studio",
  name: "",
  category: "equipment",
  base_amount: "",
  cgst_rate: "",
  sgst_rate: "",
  igst_rate: "",
  tds_rate: "0",
  expense_date: new Date().toISOString().split("T")[0],
  description: "",
  other_category: "",
};

const categories = [
  { value: "equipment", label: "Equipment" },
  { value: "software", label: "Software" },
  { value: "furniture", label: "Furniture" },
  { value: "travel", label: "Travel" },
  { value: "marketing", label: "Marketing" },
  { value: "legal", label: "Legal" },
  { value: "repairs", label: "Repairs" },
  { value: "communication", label: "Communication" },
  { value: "car-maintenance", label: "Car Maintenance" },
  { value: "fuel", label: "Fuel" },
  { value: "office-supplies", label: "Office Supplies" },
  { value: "professional-fees", label: "Professional Fees" },
  { value: "training", label: "Training" },
  { value: "subscription", label: "Subscription" },
  { value: "other", label: "Other" },
];

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const generateMonthOptions = () => {
  const options = [];
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();
  for (let i = -24; i <= 12; i++) {
    const date = new Date(currentYear, currentMonth + i, 1);
    const year = date.getFullYear();
    const month = date.getMonth();
    options.push({
      value: `${year}-${String(month + 1).padStart(2, '0')}`,
      label: `${MONTH_NAMES[month]} ${year}`,
      year,
      month
    });
  }
  return options;
};

const StatusBadge = ({ status }) => {
  const statusClasses = {
    pending: "bg-amber-100 text-amber-700",
    partial: "bg-blue-100 text-blue-700",
    paid: "bg-emerald-100 text-emerald-700",
  };
  const labels = { pending: "Pending", partial: "Partial", paid: "Paid" };
  return (
    <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${statusClasses[status] || statusClasses.pending}`}>
      {labels[status] || status}
    </span>
  );
};

export default function VariableExpenses() {
  const { canEdit } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [creditCards, setCreditCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [formData, setFormData] = useState(emptyExpense);
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterCompany, setFilterCompany] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [paymentAmounts, setPaymentAmounts] = useState({});
  const [selectedBankAccounts, setSelectedBankAccounts] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [payDialog, setPayDialog] = useState(null);
  const [payDialogAllocations, setPayDialogAllocations] = useState([{ project_id: "", line_item_id: "", amount: "", percent: "" }]);
  const [payDate, setPayDate] = useState("");
  const [tdsPaidMonths, setTdsPaidMonths] = useState(new Set());

  const currentMonthValue = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;
  const [selectedMonth, setSelectedMonth] = useState(currentMonthValue);
  const monthOptions = useMemo(() => generateMonthOptions(), []);

  const fetchExpenses = useCallback(async () => {
    try {
      const [expRes, bankRes, ccRes, statRes] = await Promise.all([
        getOneTimeExpenses(),
        getBankAccounts(),
        getCreditCards(),
        getStatutoryMonthly("all"),
      ]);
      setExpenses(expRes.data);
      setBankAccounts(bankRes.data);
      setCreditCards(ccRes.data);
      const paidSet = new Set();
      (statRes.data?.tds || []).forEach(t => { if (t.status === "paid") paidSet.add(t.month); });
      setTdsPaidMonths(paidSet);
    } catch (error) {
      toast.error("Failed to load variable expenses");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchExpenses();
  }, [fetchExpenses]);

  const handleOpenDialog = (expense = null) => {
    if (expense) {
      setSelectedExpense(expense);
      setFormData({
        company: expense.company || "empact-studio",
        name: expense.name,
        category: expense.category,
        base_amount: expense.base_amount || expense.amount || "",
        cgst_rate: expense.cgst_rate || "",
        sgst_rate: expense.sgst_rate || "",
        igst_rate: expense.igst_rate || "",
        tds_rate: expense.tds_rate?.toString() || "0",
        expense_date: expense.expense_date,
        description: expense.description || "",
        other_category: expense.category === "other" ? (expense.description || "") : "",
      });
    } else {
      setSelectedExpense(null);
      const [year, month] = selectedMonth.split('-');
      const defaultDate = `${year}-${month}-01`;
      setFormData({
        ...emptyExpense,
        company: filterCompany !== "all" ? filterCompany : "empact-studio",
        expense_date: defaultDate
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedExpense(null);
    setFormData(emptyExpense);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        company: formData.company,
        name: formData.name,
        category: formData.category,
        base_amount: parseNumericValue(formData.base_amount),
        cgst_rate: parseNumericValue(formData.cgst_rate),
        sgst_rate: parseNumericValue(formData.sgst_rate),
        igst_rate: parseNumericValue(formData.igst_rate),
        tds_rate: parseNumericValue(formData.tds_rate),
        expense_date: formData.expense_date,
        description: formData.category === "other"
          ? (formData.other_category || formData.description || null)
          : (formData.description || null),
      };

      if (selectedExpense) {
        await updateOneTimeExpense(selectedExpense.id, submitData);
        toast.success("Expense updated successfully");
      } else {
        await createOneTimeExpense(submitData);
        toast.success("Expense added successfully");
      }
      handleCloseDialog();
      fetchExpenses();
    } catch (error) {
      toast.error("Failed to save expense");
    }
  };

  const handleDelete = async () => {
    try {
      await deleteOneTimeExpense(selectedExpense.id);
      toast.success("Expense deleted successfully");
      setDeleteDialogOpen(false);
      setSelectedExpense(null);
      fetchExpenses();
    } catch (error) {
      toast.error("Failed to delete expense");
    }
  };

  const handlePaymentAmountChange = (id, value) => {
    setPaymentAmounts(prev => ({ ...prev, [id]: handleNumericInput(value) }));
  };

  const handleBankAccountChange = (id, bankAccountId) => {
    setSelectedBankAccounts(prev => ({ ...prev, [id]: bankAccountId }));
  };

  const handleRecordPayment = async (expense) => {
    const amountStr = paymentAmounts[expense.id];
    const amount = parseNumericValue(amountStr);
    const bankAccountId = selectedBankAccounts[expense.id];

    if (!amount || amount <= 0) {
      toast.error("Please enter a valid payment amount");
      return;
    }
    if (!bankAccountId) {
      toast.error("Please select a bank account");
      return;
    }

    // Open payment dialog for optional project allocation
    setPayDialog(expense);
    setPayDialogAllocations([{ project_id: "", line_item_id: "", amount: "", percent: "" }]);
  };

  const handleConfirmPayment = async () => {
    const expense = payDialog;
    const amountStr = paymentAmounts[expense.id];
    const amount = parseNumericValue(amountStr);
    const bankAccountId = selectedBankAccounts[expense.id];

    const validAllocs = payDialogAllocations.filter(a => a.project_id && a.line_item_id && parseFloat(a.amount) > 0);
    const projectAllocPayload = validAllocs.length > 0
      ? { project_allocations: validAllocs.map(a => ({ project_id: a.project_id, line_item_id: a.line_item_id, amount: parseFloat(a.amount) })) }
      : null;

    try {
      setSavingId(expense.id);
      await recordOneTimeExpensePayment(expense.id, amount, bankAccountId, projectAllocPayload, payDate);
      toast.success(`Payment of ${formatCurrency(amount)} recorded`);
      setPaymentAmounts(prev => { const s = { ...prev }; delete s[expense.id]; return s; });
      setSelectedBankAccounts(prev => { const s = { ...prev }; delete s[expense.id]; return s; });
      setPayDialog(null);
      setPayDate("");
      fetchExpenses();
    } catch (error) {
      toast.error("Failed to record payment");
    } finally {
      setSavingId(null);
    }
  };

  const handleResetPayment = async (expense) => {
    try {
      setSavingId(expense.id);
      await resetOneTimeExpensePayment(expense.id);
      toast.success("Payment reset (bank balance restored)");
      fetchExpenses();
    } catch (error) {
      toast.error("Failed to reset payment");
    } finally {
      setSavingId(null);
    }
  };

  const navigateMonth = (direction) => {
    const currentIndex = monthOptions.findIndex(m => m.value === selectedMonth);
    const newIndex = currentIndex + direction;
    if (newIndex >= 0 && newIndex < monthOptions.length) {
      setSelectedMonth(monthOptions[newIndex].value);
    }
  };

  const filteredExpenses = expenses.filter((exp) => {
    const monthMatch = exp.expense_date.startsWith(selectedMonth);
    const categoryMatch = filterCategory === "all" || exp.category === filterCategory;
    const companyMatch = filterCompany === "all" || exp.company === filterCompany;
    const statusMatch = filterStatus === "all" || exp.status === filterStatus;
    return monthMatch && categoryMatch && companyMatch && statusMatch;
  });

  // Summary totals for filtered expenses
  const totalNetPayable = filteredExpenses.reduce((sum, exp) => sum + (exp.net_payable || exp.amount || 0), 0);
  const totalPaidSoFar = filteredExpenses.reduce((sum, exp) => sum + (exp.amount_paid || 0), 0);
  const totalBalance = filteredExpenses.reduce((sum, exp) => sum + (exp.balance ?? ((exp.net_payable || exp.amount || 0) - (exp.amount_paid || 0))), 0);

  const companyTotals = COMPANY_OPTIONS.reduce((acc, company) => {
    acc[company.value] = expenses
      .filter((exp) => exp.expense_date.startsWith(selectedMonth) && exp.company === company.value && exp.status !== "paid")
      .reduce((sum, exp) => sum + (exp.balance ?? ((exp.net_payable || exp.amount || 0) - (exp.amount_paid || 0))), 0);
    return acc;
  }, {});

  const selectedMonthLabel = monthOptions.find(m => m.value === selectedMonth)?.label || selectedMonth;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="variable-expenses-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Variable Expenses</h1>
          <p className="text-sm text-slate-500 mt-1">
            Track non-recurring and variable expenses
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setExportDialogOpen(true)}
            data-testid="export-variable-expenses"
          >
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
          {canEdit && (
            <Button
              onClick={() => handleOpenDialog()}
              className="bg-brand-orange hover:bg-[#e09515] text-brand-navy"
              data-testid="add-variable-expense-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Expense
            </Button>
          )}
        </div>
      </div>

      {/* Month Selector and Summary */}
      <Card className="bg-brand-navy text-white">
        <CardContent className="p-4 sm:p-6">
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-center gap-2 sm:gap-3">
              <Button variant="ghost" size="icon" className="text-white hover:bg-white/10 h-8 w-8" onClick={() => navigateMonth(-1)} data-testid="prev-month-btn">
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                <SelectTrigger className="w-36 sm:w-40 bg-white/10 border-white/20 text-white" data-testid="select-month">
                  <Calendar className="w-4 h-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {monthOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="ghost" size="icon" className="text-white hover:bg-white/10 h-8 w-8" onClick={() => navigateMonth(1)} data-testid="next-month-btn">
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>
            <div className="grid grid-cols-3 gap-3 sm:gap-6 text-center">
              <div>
                <p className="text-white/70 text-[10px] sm:text-sm">Net Payable</p>
                <p className="text-sm sm:text-xl md:text-2xl font-bold tabular-nums">{formatCurrency(totalNetPayable)}</p>
              </div>
              <div>
                <p className="text-white/70 text-[10px] sm:text-sm">Paid So Far</p>
                <p className="text-sm sm:text-xl md:text-2xl font-bold tabular-nums text-emerald-300">{formatCurrency(totalPaidSoFar)}</p>
              </div>
              <div>
                <p className="text-white/70 text-[10px] sm:text-sm">Balance</p>
                <p className="text-sm sm:text-xl md:text-2xl font-bold tabular-nums text-amber-300">{formatCurrency(totalBalance)}</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-6 pt-4 border-t border-white/10">
            {COMPANY_OPTIONS.map((company) => (
              <div key={company.value} className="bg-white/10 rounded-lg p-3">
                <p className="text-white/70 text-xs font-medium">{company.label}</p>
                <p className="text-sm sm:text-xl font-bold tabular-nums mt-1">{formatCurrency(companyTotals[company.value] || 0)}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <p className="text-sm text-slate-500">{filteredExpenses.length} expenses in {selectedMonthLabel}</p>
            <div className="flex flex-wrap gap-2">
              <Select value={filterCompany} onValueChange={setFilterCompany}>
                <SelectTrigger className="w-40" data-testid="filter-variable-company">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COMPANIES.map((company) => (
                    <SelectItem key={company.value} value={company.value}>{company.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="w-40" data-testid="filter-variable-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-32" data-testid="filter-variable-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="partial">Partial</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      {filteredExpenses.length > 0 ? (
        <Card>
          <div className="overflow-x-auto">
            <Table className="min-w-[1100px]">
              <TableHeader>
                <TableRow>
                  <TableHead>Company</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead className="text-right">Base Amount</TableHead>
                  <TableHead className="text-right">GST</TableHead>
                  <TableHead className="text-right">TDS</TableHead>
                  <TableHead className="text-right">Net Payable</TableHead>
                  <TableHead className="text-right w-40">Pay Amount</TableHead>
                  <TableHead className="text-right">Paid So Far</TableHead>
                  <TableHead className="text-right">Balance</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-center w-28">Action</TableHead>
                  <TableHead className="text-right">Edit</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredExpenses.map((expense) => {
                  const baseAmount = expense.base_amount || expense.amount || 0;
                  const gstAmount = (expense.cgst_amount || 0) + (expense.sgst_amount || 0) + (expense.igst_amount || 0);
                  const tdsAmount = expense.tds_amount || 0;
                  const netPayable = expense.net_payable || expense.amount || 0;
                  const paidSoFar = expense.amount_paid || 0;
                  const balance = expense.balance ?? (netPayable - paidSoFar);
                  const isFullyPaid = balance <= 0;
                  const paymentAmount = paymentAmounts[expense.id] || "";

                  return (
                    <TableRow
                      key={expense.id}
                      data-testid={`variable-row-${expense.id}`}
                      className={isFullyPaid ? "bg-emerald-50/50" : expense.status === "partial" ? "bg-blue-50/50" : ""}
                    >
                      <TableCell>
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${COMPANY_COLORS[expense.company] || COMPANY_COLORS.all}`}>
                          {getCompanyLabel(expense.company)}
                        </span>
                      </TableCell>
                      <TableCell className="text-slate-600">{formatDate(expense.expense_date)}</TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{expense.name}</p>
                          {expense.description && <p className="text-xs text-slate-500 truncate max-w-[150px]">{expense.description}</p>}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700 capitalize">
                          {expense.category}
                        </span>
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-slate-700">
                        {formatCurrency(baseAmount)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-blue-600">
                        {gstAmount > 0 ? (
                          <>
                            +{formatCurrency(gstAmount)}
                            <span className="text-xs text-blue-400 block">
                              {expense.cgst_amount > 0 && `C:${expense.cgst_rate}%`}
                              {expense.sgst_amount > 0 && ` S:${expense.sgst_rate}%`}
                              {expense.igst_amount > 0 && ` I:${expense.igst_rate}%`}
                            </span>
                          </>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-emerald-600">
                        {(() => {
                          const month = expense.expense_date ? expense.expense_date.slice(0, 7) : (expense.created_at ? expense.created_at.slice(0, 7) : "");
                          const isTdsPaid = tdsPaidMonths.has(month);
                          return tdsAmount > 0 ? (
                            <div className={isTdsPaid ? "line-through text-red-500" : ""}>
                              -{formatCurrency(tdsAmount)}
                              <span className={`text-xs block ${isTdsPaid ? "text-red-400" : "text-emerald-400"}`}>
                                @{expense.tds_rate}% {isTdsPaid && "· Paid"}
                              </span>
                            </div>
                          ) : (
                            <span className="text-slate-400">-</span>
                          );
                        })()}
                      </TableCell>
                      <TableCell className="text-right font-semibold tabular-nums text-brand-navy">
                        {formatCurrency(netPayable)}
                      </TableCell>
                      <TableCell className="text-right">
                        {!isFullyPaid ? (
                          <div className="flex flex-col gap-1">
                            <div className="relative inline-flex">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">&#8377;</span>
                              <Input
                                type="text"
                                inputMode="decimal"
                                value={paymentAmount}
                                onChange={(e) => handlePaymentAmountChange(expense.id, e.target.value)}
                                onBlur={(e) => {
                                  const val = parseNumericValue(e.target.value);
                                  if (val > 0) {
                                    setPaymentAmounts(prev => ({...prev, [expense.id]: formatIndianNumber(val)}));
                                  }
                                }}
                                className="text-right pr-3 pl-7 w-36 font-medium"
                                placeholder={formatIndianNumber(balance)}
                                data-testid={`payment-input-${expense.id}`}
                              />
                            </div>
                            <Select
                              value={selectedBankAccounts[expense.id] || ""}
                              onValueChange={(value) => handleBankAccountChange(expense.id, value)}
                            >
                              <SelectTrigger className="w-36 h-8 text-xs" data-testid={`bank-select-${expense.id}`}>
                                <SelectValue placeholder="Pay from..." />
                              </SelectTrigger>
                              <SelectContent>
                                {bankAccounts.length > 0 && (
                                  <>
                                    <SelectItem value="__bank_label" disabled className="text-xs font-semibold text-slate-400">Bank Accounts</SelectItem>
                                    {bankAccounts.map((bank) => (
                                      <SelectItem key={bank.id} value={bank.id} className="text-xs">
                                        {bank.name} ({formatCurrency(bank.balance)})
                                      </SelectItem>
                                    ))}
                                  </>
                                )}
                                {creditCards.length > 0 && (
                                  <>
                                    <SelectItem value="__cc_label" disabled className="text-xs font-semibold text-slate-400">Credit Cards</SelectItem>
                                    {creditCards.map((cc) => (
                                      <SelectItem key={`cc-${cc.id}`} value={`cc-${cc.id}`} className="text-xs">
                                        <span className="flex items-center gap-1">
                                          <CreditCard className="w-3 h-3" />
                                          {cc.card_name}
                                        </span>
                                      </SelectItem>
                                    ))}
                                  </>
                                )}
                              </SelectContent>
                            </Select>
                          </div>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        <span className={paidSoFar > 0 ? "text-emerald-600 font-semibold" : "text-slate-400"}>
                          {formatCurrency(paidSoFar)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        <span className={balance > 0 ? "text-amber-600 font-semibold" : "text-emerald-600 font-semibold"}>
                          {formatCurrency(balance)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={expense.status || "pending"} />
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex flex-col gap-1 items-center">
                          {!isFullyPaid && (
                            <Button
                              onClick={() => handleRecordPayment(expense)}
                              disabled={savingId === expense.id || !paymentAmount}
                              size="sm"
                              className="bg-emerald-600 hover:bg-emerald-700 text-white h-9 px-3 w-full"
                              data-testid={`pay-btn-${expense.id}`}
                            >
                              {savingId === expense.id ? <RefreshCw className="w-4 h-4 animate-spin" /> : "Pay"}
                            </Button>
                          )}
                          {paidSoFar > 0 && (
                            <Button
                              onClick={() => handleResetPayment(expense)}
                              disabled={savingId === expense.id}
                              size="sm"
                              variant="outline"
                              className="h-8 px-3 text-xs w-full"
                              data-testid={`reset-btn-${expense.id}`}
                            >
                              {savingId === expense.id ? <RefreshCw className="w-3 h-3 animate-spin" /> : "Undo"}
                            </Button>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleOpenDialog(expense)} data-testid={`edit-variable-${expense.id}`}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-600"
                            onClick={() => { setSelectedExpense(expense); setDeleteDialogOpen(true); }}
                            data-testid={`delete-variable-${expense.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </Card>
      ) : (
        <Card>
          <CardContent className="py-12">
            <div className="empty-state">
              <Receipt className="empty-state-icon" />
              <h3 className="empty-state-title">No expenses for {selectedMonthLabel}</h3>
              <p className="empty-state-text">
                Track equipment purchases, software licenses, and other variable costs
              </p>
              <Button onClick={() => handleOpenDialog()} className="mt-4 bg-brand-orange hover:bg-[#e09515] text-brand-navy">
                <Plus className="w-4 h-4 mr-2" />
                Add Expense
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="variable-expense-dialog">
          <DialogHeader>
            <DialogTitle>{selectedExpense ? "Edit Expense" : "Add Variable Expense"}</DialogTitle>
            <DialogDescription>{selectedExpense ? "Update the expense details below." : "Fill in the details to add a variable expense."}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="company">Company</Label>
              <Select value={formData.company} onValueChange={(value) => setFormData({ ...formData, company: value })}>
                <SelectTrigger data-testid="select-variable-company"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {COMPANY_OPTIONS.map((company) => (
                    <SelectItem key={company.value} value={company.value}>{company.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="name">Expense Name</Label>
              <Input id="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="e.g., MacBook Pro, Office Chair" required data-testid="input-variable-name" />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="category">Category</Label>
                <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value, other_category: value !== "other" ? "" : formData.other_category })}>
                  <SelectTrigger data-testid="select-variable-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="expense_date">Date</Label>
                <DatePicker
                  value={formData.expense_date}
                  onChange={(val) => setFormData({ ...formData, expense_date: val })}
                  data-testid="input-variable-date"
                />
              </div>
            </div>

            {formData.category === "other" && (
              <div>
                <Label htmlFor="other_category">Specify "Other" Category</Label>
                <Input id="other_category" value={formData.other_category} onChange={(e) => setFormData({ ...formData, other_category: e.target.value })} placeholder="What is this expense for?" required data-testid="input-other-variable-category" />
              </div>
            )}

            <div>
              <Label htmlFor="base_amount">Base Amount (&#8377;)</Label>
              <Input id="base_amount" type="text" inputMode="decimal" value={formData.base_amount} onChange={(e) => setFormData({ ...formData, base_amount: handleNumericInput(e.target.value) })} placeholder="Enter amount" required data-testid="input-variable-amount" />
            </div>

            <GSTInput
              cgst_rate={formData.cgst_rate}
              sgst_rate={formData.sgst_rate}
              igst_rate={formData.igst_rate}
              handleNumericInput={handleNumericInput}
              onChange={(vals) => setFormData((prev) => ({ ...prev, ...vals }))}
            />

            <div>
              <Label htmlFor="tds_rate" className="text-red-600">TDS Rate (%)</Label>
              <Select value={formData.tds_rate?.toString() || "0"} onValueChange={(value) => setFormData({ ...formData, tds_rate: value })}>
                <SelectTrigger data-testid="input-variable-tds"><SelectValue placeholder="Select TDS Rate" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">No TDS</SelectItem>
                  <SelectItem value="1">1% - Insurance Commission</SelectItem>
                  <SelectItem value="2">2% - Contractors/Professionals</SelectItem>
                  <SelectItem value="5">5% - Rent (below threshold)</SelectItem>
                  <SelectItem value="10">10% - Professional Fees/Rent</SelectItem>
                  <SelectItem value="20">20% - No PAN</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">TDS will be deducted from base amount</p>
            </div>

            {formData.category !== "other" && (
              <div>
                <Label htmlFor="description">Description (Optional)</Label>
                <Textarea id="description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Additional details..." rows={2} data-testid="input-variable-description" />
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>Cancel</Button>
              <Button type="submit" className="bg-brand-orange hover:bg-[#e09515] text-brand-navy" data-testid="save-variable-expense">
                {selectedExpense ? "Update" : "Add"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Expense</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedExpense?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600" data-testid="confirm-delete-variable">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Payment Project Allocation Dialog */}
      <Dialog open={!!payDialog} onOpenChange={(open) => { if (!open) setPayDialog(null); }}>
        <DialogContent className="max-w-md" data-testid="pay-project-dialog">
          <DialogHeader>
            <DialogTitle>Confirm Payment</DialogTitle>
          </DialogHeader>
          {payDialog && (
            <div className="space-y-4 pt-2">
              <div className="p-3 bg-slate-50 rounded-lg text-sm space-y-1">
                <div className="flex justify-between"><span className="text-slate-500">Expense</span><span className="font-medium">{payDialog.name}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Amount</span><span className="font-bold text-brand-navy">{formatCurrency(parseNumericValue(paymentAmounts[payDialog.id]))}</span></div>
              </div>

              <ProjectAllocation
                allocations={payDialogAllocations}
                totalAmount={parseNumericValue(paymentAmounts[payDialog.id])}
                onChange={setPayDialogAllocations}
              />

              <div>
                <Label className="text-sm">Payment Date</Label>
                <DatePicker value={payDate} onChange={(v) => setPayDate(v)} data-testid="var-pay-date-picker" />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => { setPayDialog(null); setPayDate(""); }}>Cancel</Button>
                <Button onClick={handleConfirmPayment} disabled={savingId === payDialog.id} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="confirm-pay-btn">
                  {savingId === payDialog.id ? <RefreshCw className="w-4 h-4 animate-spin" /> : "Confirm Payment"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Export Dialog */}
      <ExportDialog
        open={exportDialogOpen}
        onOpenChange={setExportDialogOpen}
        dataType="onetime-expenses"
        title="Variable Expenses"
        showDateFilter={true}
        showCompanyFilter={true}
      />
    </div>
  );
}
